import React, { FC } from 'react';
import { Upload } from 'antd';
import {
  UploadOutlined, DeleteOutlined, LoadingOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import type { UploadChangeParam } from 'antd/es/upload';
import type { MedicalHistoryValues, UploadedFile } from '../types/flow';
import { FLabel, SectionDivider } from '../../assessment-portal/components/form-fields';
import {
  SUPPORTING_DOCUMENT_LABELS,
  ACCEPTED_FILE_TYPES,
} from '../data/constants';

interface DocumentUploadFormProps {
  values: MedicalHistoryValues;
  setValue: (field: keyof MedicalHistoryValues, value: any) => void;
  handleDocUpload: (index: number, info: UploadChangeParam) => void;
  uploadingDoc: Record<number, boolean>;
  uploadAction: string;
  uploadHeaders: Record<string, string>;
}

const DocumentUploadForm: FC<DocumentUploadFormProps> = ({
  values,
  setValue,
  handleDocUpload,
  uploadingDoc,
  uploadAction,
  uploadHeaders,
}) => {
  const docLabels = SUPPORTING_DOCUMENT_LABELS;

  return (
    <>
      <h2 className="text-xl font-bold mb-1 text-brand-dark">Medical History — Part 4 of 4</h2>
      <p className="text-slate-500 text-sm mb-6">Upload any supporting documents (optional but recommended).</p>

      <div className="space-y-5">
        <SectionDivider>Supporting Documents</SectionDivider>
        <div className="rounded-xl p-4 border border-amber-200 bg-amber-50 text-xs text-amber-800 leading-relaxed">
          <p className="font-semibold mb-1">📄 Upload additional documents (optional but recommended)</p>
          <p>Uploading documents from childhood — such as teacher or school reports, or past paediatrician / psychiatry / psychology letters — may assist with your assessment. Take clear, high-resolution photos of these documents.</p>
          <p className="mt-2">
            If you need to submit them later, please email them to:
            <strong>adhd@telecare.com.au</strong>
          </p>
        </div>

        {docLabels.map((doc, i) => (
          <div key={doc.label} className="adhd-mq-card">
            <FLabel>{doc.label}</FLabel>
            {'description' in doc && (
              <p className="text-xs text-slate-500 mt-0.5 mb-1">{doc.description}</p>
            )}
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              {values.docs[i] ? (
                <div className="flex items-center gap-2 flex-1 px-3.5 py-2.5 border-2 border-green-200 rounded-xl bg-green-50 text-sm">
                  <CheckCircleOutlined className="text-green-500" />
                  <span className="text-slate-700 truncate flex-1">{(values.docs[i] as UploadedFile).fileName}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const d = [...values.docs];
                      d[i] = null;
                      setValue('docs', d);
                    }}
                    className="text-red-400 hover:text-red-600 flex-shrink-0 min-h-yn-btn"
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex-1 px-3.5 py-2.5 border-2 border-slate-200 rounded-xl text-sm text-slate-400 bg-white min-h-yn-btn flex items-center">No file chosen</div>
                  <Upload
                    accept={ACCEPTED_FILE_TYPES}
                    action={uploadAction}
                    headers={uploadHeaders}
                    showUploadList={false}
                    onChange={(info) => handleDocUpload(i, info)}
                  >
                    <button type="button" className="w-full md:w-auto flex-shrink-0 px-4 py-2.5 rounded-xl text-white text-sm font-semibold bg-brand flex items-center justify-center gap-1.5 cursor-pointer min-h-yn-btn">
                      {uploadingDoc[i] ? <LoadingOutlined /> : <UploadOutlined />}
                      {uploadingDoc[i] ? 'Uploading...' : 'Browse'}
                    </button>
                  </Upload>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default DocumentUploadForm;
